import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowForward,
    VideoLibrary as VideoLibraryIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Videocam as VideocamIcon,
    Edit as EditIcon,
    Check as CheckIcon
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useMessageContext } from "../contexts/MessageContext";
import { RTL } from "../cache/CacheRtl";
import Loader from "../componenets/Loader";
import UploadProgressIndicator from "../componenets/common/UploadProgressIndicator";
import { uploadVideo } from "../services/MediaUploadService";
import {
    getProgressVideos,
    addProgressVideo,
    deleteProgressVideo,
    deleteProgressVideosByDate,
    saveVideoDateComment
} from "../services/ProgressVideosService";
import { formatMediaDate, getHebrewDayName, toISODateKey } from "../utils/progressMediaDate";

const PAGE_SIZE = 20;

// Date parsing/formatting is shared with ProgressPhotosPage via progressMediaDate:
// the backend serializes videoDate as a LocalDate array ([y,m,d]), which raw
// `new Date()` mishandles cross-browser and which stringifies to a "2026,5,19" key the
// delete-by-date / save-comment endpoints can't parse. See utils/progressMediaDate.js.
const formatDate = formatMediaDate;
const getDayName = getHebrewDayName;

// --- Video Thumbnail Card ---
const VideoThumbnail = React.memo(({ video, onDelete }) => {
    const [hover, setHover] = useState(false);

    return (
        <Box
            sx={{
                position: 'relative',
                width: 240,
                height: 180,
                borderRadius: '10px',
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: '#000',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.2s'
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <video
                src={video.videoUrl}
                controls
                preload="metadata"
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                }}
            />
            {hover && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        p: 0.5
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onDelete(video); }}
                        sx={{
                            backgroundColor: 'rgba(239,68,68,0.9)',
                            color: 'white',
                            '&:hover': { backgroundColor: '#EF4444' }
                        }}
                    >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
});

// --- Add Video Skeleton Card ---
const AddVideoSkeleton = ({ onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            width: 240,
            height: 180,
            borderRadius: '10px',
            border: '2px dashed #CBD5E1',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'all 0.2s',
            '&:hover': {
                borderColor: '#14B8A6',
                backgroundColor: '#F0FDFA'
            }
        }}
    >
        <UploadIcon sx={{ fontSize: 28, color: '#94A3B8', mb: 0.5 }} />
        <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontWeight: 500 }}>
            הוסף סרטון
        </Typography>
    </Box>
);

// --- Date Group ---
const DateGroup = React.memo(({ date, videos, comment, onDeleteVideo, onDeleteDate, onAddVideo, editingComment, editCommentText, onEditComment, onEditCommentChange, onSaveComment, onCancelEditComment }) => {
    const isEditing = editingComment === date;
    return (
        <Box sx={{ mb: 4 }}>
            {/* Date Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                pb: 1,
                borderBottom: '1px solid #E2E8F0'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>
                        {formatDate(date)}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#94A3B8' }}>
                        יום {getDayName(date)}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#64748B', backgroundColor: '#F1F5F9', borderRadius: '12px', px: 1.5, py: 0.3 }}>
                        {videos.length} {videos.length === 1 ? 'סרטון' : 'סרטונים'}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={() => onDeleteDate(date, videos.length)}
                    sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' } }}
                >
                    <DeleteIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {/* Comment */}
            {isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <RTL>
                        <TextField
                            value={editCommentText}
                            onChange={(e) => onEditCommentChange(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="הוסף הערה..."
                            inputProps={{ maxLength: 1000 }}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') onSaveComment(); if (e.key === 'Escape') onCancelEditComment(); }}
                        />
                    </RTL>
                    <IconButton size="small" onClick={onSaveComment} sx={{ color: '#14B8A6' }}>
                        <CheckIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton size="small" onClick={onCancelEditComment} sx={{ color: '#94A3B8' }}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                    {comment ? (
                        <Typography sx={{ fontSize: '13px', color: '#64748B', fontStyle: 'italic' }}>
                            {comment}
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: '12px', color: '#CBD5E1' }}>
                            ללא הערה
                        </Typography>
                    )}
                    <IconButton size="small" onClick={() => onEditComment(date, comment)} sx={{ color: '#94A3B8', '&:hover': { color: '#14B8A6' } }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            )}

            {/* Video Row */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5
            }}>
                {videos.map(video => (
                    <VideoThumbnail
                        key={video.id}
                        video={video}
                        onDelete={onDeleteVideo}
                    />
                ))}
                <AddVideoSkeleton onClick={() => onAddVideo(date)} />
            </Box>
        </Box>
    );
});

// --- Upload Dialog ---
const UploadDialog = ({ open, onClose, traineeId, onUploadComplete, initialDate }) => {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [files, setFiles] = useState([]);
    const [comment, setComment] = useState('');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedDate(initialDate ? dayjs(initialDate) : dayjs());
        }
    }, [open, initialDate]);
    const [uploadPhase, setUploadPhase] = useState('idle');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);

    const { notificationHandler } = useMessageContext();

    const handleFileChange = (e) => {
        if (e.target.files) {
            setFiles(Array.from(e.target.files));
        }
    };

    const handleUpload = async () => {
        if (files.length === 0) return;

        setUploading(true);
        setTotalFiles(files.length);
        const dateStr = selectedDate.format('YYYY-MM-DD');

        try {
            for (let i = 0; i < files.length; i++) {
                setCurrentFileIndex(i + 1);
                setUploadPhase('uploading');
                setUploadProgress(0);

                const cdnUrl = await uploadVideo(
                    files[i],
                    (percent) => { setUploadPhase('uploading'); setUploadProgress(percent); },
                    (percent) => { setUploadPhase('compressing'); setUploadProgress(percent); }
                );

                await addProgressVideo(traineeId, cdnUrl, dateStr);
            }

            if (comment.trim()) {
                await saveVideoDateComment(traineeId, dateStr, comment.trim());
            }

            setUploadPhase('complete');
            notificationHandler.success(`${files.length} סרטונים הועלו בהצלחה`);
            onUploadComplete();
            setTimeout(() => {
                handleClose();
            }, 500);
        } catch (error) {
            console.error('Upload error:', error);
            setUploadPhase('error');
            notificationHandler.error('שגיאה בהעלאת הסרטונים');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (uploading) return;
        setFiles([]);
        setComment('');
        setUploadPhase('idle');
        setUploadProgress(0);
        setCurrentFileIndex(0);
        setSelectedDate(dayjs());
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{
            sx: { borderRadius: '16px', direction: 'rtl' }
        }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '18px', color: '#1E293B', borderBottom: '1px solid #E2E8F0' }}>
                העלאת סרטוני התקדמות
            </DialogTitle>
            <DialogContent sx={{ pt: '20px !important' }}>
                {/* Date Picker */}
                <Box sx={{ mb: 3 }}>
                    <RTL>
                        <DatePicker
                            label="תאריך צילום"
                            value={selectedDate}
                            onChange={(val) => val && setSelectedDate(val)}
                            format="DD/MM/YYYY"
                            maxDate={dayjs()}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </RTL>
                </Box>

                {/* Notes */}
                <Box sx={{ mb: 3 }}>
                    <RTL>
                        <TextField
                            label="הערות (אופציונלי)"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            disabled={uploading}
                            inputProps={{ maxLength: 1000 }}
                        />
                    </RTL>
                </Box>

                {/* File Input */}
                <Box
                    component="label"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4,
                        border: '2px dashed #CBD5E1',
                        borderRadius: '12px',
                        cursor: uploading ? 'default' : 'pointer',
                        backgroundColor: '#F8FAFC',
                        transition: 'all 0.2s',
                        '&:hover': uploading ? {} : {
                            borderColor: '#14B8A6',
                            backgroundColor: '#F0FDFA'
                        },
                        mb: 2
                    }}
                >
                    <input
                        type="file"
                        accept="video/*"
                        multiple
                        hidden
                        disabled={uploading}
                        onChange={handleFileChange}
                    />
                    <VideocamIcon sx={{ fontSize: 40, color: '#94A3B8', mb: 1 }} />
                    <Typography sx={{ fontSize: '14px', color: '#64748B', fontWeight: 500 }}>
                        {files.length > 0 ? `${files.length} קבצים נבחרו` : 'לחץ לבחירת סרטונים'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#94A3B8', mt: 0.5 }}>
                        ניתן לבחור מספר סרטונים
                    </Typography>
                </Box>

                {/* File list preview */}
                {files.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                        {files.map((file, idx) => (
                            <Box key={idx} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 0.5,
                                backgroundColor: '#F1F5F9',
                                borderRadius: '8px'
                            }}>
                                <VideocamIcon sx={{ fontSize: 18, color: '#64748B' }} />
                                <Typography sx={{ fontSize: '13px', color: '#64748B', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.name}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>
                                    {(file.size / (1024 * 1024)).toFixed(1)} MB
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Upload Progress */}
                {uploadPhase !== 'idle' && (
                    <Box sx={{ mb: 1 }}>
                        {totalFiles > 1 && (
                            <Typography sx={{ fontSize: '12px', color: '#64748B', mb: 1, textAlign: 'center' }}>
                                מעלה {currentFileIndex}/{totalFiles}
                            </Typography>
                        )}
                        <UploadProgressIndicator
                            phase={uploadPhase}
                            uploadProgress={uploadProgress}
                            compressionProgress={uploadPhase === 'compressing' ? uploadProgress : 0}
                            compact
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #E2E8F0' }}>
                <Button onClick={handleClose} disabled={uploading} sx={{ color: '#64748B', textTransform: 'none' }}>
                    ביטול
                </Button>
                <Button
                    onClick={handleUpload}
                    disabled={files.length === 0 || uploading}
                    variant="contained"
                    sx={{
                        backgroundColor: '#14B8A6',
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 500,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#0D9488', boxShadow: 'none' },
                        '&:disabled': { backgroundColor: '#E2E8F0' }
                    }}
                >
                    {uploading ? 'מעלה...' : 'העלאה'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// === MAIN PAGE ===
const ProgressVideosPage = () => {
    const navigate = useNavigate();
    const { userId } = useParams();

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadPrefilledDate, setUploadPrefilledDate] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [editingComment, setEditingComment] = useState(null);
    const [editCommentText, setEditCommentText] = useState('');

    const { notificationHandler } = useMessageContext();

    const fetchVideos = async (pageId = 0, append = false) => {
        setLoading(true);
        try {
            const response = await getProgressVideos(userId, pageId, PAGE_SIZE);
            const pageData = response.data.body;
            const content = pageData.content || [];

            if (append) {
                setVideos(prev => [...prev, ...content]);
            } else {
                setVideos(content);
            }
            setHasMore(!pageData.last);
            setPage(pageId);
        } catch (err) {
            console.error('Error fetching videos:', err);
            notificationHandler.error('שגיאה בטעינת הסרטונים');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchVideos(0);
        }
    }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Group videos by date
    const videosByDate = useMemo(() => {
        const grouped = {};
        videos.forEach(video => {
            // Canonical YYYY-MM-DD key (not the raw "2026,5,19" array-stringification):
            // this key becomes group.date, which is sent to deleteProgressVideosByDate /
            // saveVideoDateComment — endpoints that expect a YYYY-MM-DD LocalDate.
            const dateKey = toISODateKey(video.videoDate);
            if (!grouped[dateKey]) {
                grouped[dateKey] = { videos: [], comment: '' };
            }
            grouped[dateKey].videos.push(video);
            if (video.dateComment) grouped[dateKey].comment = video.dateComment;
        });
        return Object.entries(grouped)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([date, data]) => ({ date, videos: data.videos, comment: data.comment }));
    }, [videos]);

    const handleLoadMore = () => {
        if (hasMore && !loading) {
            fetchVideos(page + 1, true);
        }
    };

    // Delete
    const handleDeleteVideo = (video) => {
        setDeleteConfirm({ type: 'single', video });
    };

    const handleDeleteDate = (date, count) => {
        setDeleteConfirm({ type: 'date', date, count });
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        setLoading(true);
        try {
            if (deleteConfirm.type === 'single') {
                await deleteProgressVideo(userId, deleteConfirm.video.id);
                notificationHandler.success('הסרטון נמחק');
            } else {
                await deleteProgressVideosByDate(userId, deleteConfirm.date);
                notificationHandler.success('כל הסרטונים מהתאריך נמחקו');
            }
            setDeleteConfirm(null);
            fetchVideos(0);
        } catch (err) {
            console.error('Delete error:', err);
            notificationHandler.error('שגיאה במחיקה');
        } finally {
            setLoading(false);
        }
    };

    // Upload complete
    const handleUploadComplete = () => {
        fetchVideos(0);
    };

    // Add video to specific date
    const handleAddVideoToDate = (date) => {
        setUploadPrefilledDate(date);
        setUploadDialogOpen(true);
    };

    const handleUploadDialogClose = () => {
        setUploadDialogOpen(false);
        setUploadPrefilledDate(null);
    };

    // Edit comment
    const handleEditComment = (date, currentComment) => {
        setEditingComment(date);
        setEditCommentText(currentComment || '');
    };

    const handleSaveComment = async () => {
        if (!editingComment) return;
        try {
            await saveVideoDateComment(userId, editingComment, editCommentText.trim() || null);
            notificationHandler.success('ההערה עודכנה');
            setEditingComment(null);
            fetchVideos(0);
        } catch (err) {
            console.error('Error saving comment:', err);
            notificationHandler.error('שגיאה בשמירת ההערה');
        }
    };

    const hasData = videos.length > 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Loader loading={loading} />

            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, pt: 2, mb: 2 }}>
                <IconButton
                    onClick={() => navigate(`/app/clients/${userId}`)}
                    sx={{
                        color: "#64748B",
                        "&:hover": { backgroundColor: "#F1F5F9", color: "#1E293B" }
                    }}
                >
                    <ArrowForward />
                </IconButton>
                <VideoLibraryIcon sx={{ color: '#14B8A6', fontSize: 28 }} />
                <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1E293B" }}>
                    סרטוני התקדמות
                </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ px: 3, mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
                <RTL>
                    <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{
                            backgroundColor: "#14B8A6",
                            color: "white",
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "8px 20px",
                            fontWeight: 500,
                            fontSize: "14px",
                            boxShadow: "none",
                            '&:hover': { backgroundColor: "#0D9488", boxShadow: "none" }
                        }}
                    >
                        העלאת סרטון
                    </Button>
                </RTL>
            </Box>

            {/* Content */}
            <Box sx={{ margin: "0 40px 40px" }}>
                {/* Gallery */}
                {!hasData && !loading ? (
                    <Box sx={{
                        textAlign: 'center',
                        py: 8,
                        backgroundColor: '#F8FAFC',
                        borderRadius: '16px'
                    }}>
                        <VideocamIcon sx={{ fontSize: 56, color: '#CBD5E1', mb: 2 }} />
                        <Typography sx={{ color: '#94A3B8', fontSize: '16px', mb: 1 }}>
                            אין סרטוני התקדמות עדיין
                        </Typography>
                        <Typography sx={{ color: '#CBD5E1', fontSize: '14px' }}>
                            לחץ על "העלאת סרטון" כדי להוסיף סרטונים
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {videosByDate.map(group => (
                            <DateGroup
                                key={group.date}
                                date={group.date}
                                videos={group.videos}
                                comment={group.comment}
                                onDeleteVideo={handleDeleteVideo}
                                onDeleteDate={handleDeleteDate}
                                onAddVideo={handleAddVideoToDate}
                                editingComment={editingComment}
                                editCommentText={editCommentText}
                                onEditComment={handleEditComment}
                                onEditCommentChange={setEditCommentText}
                                onSaveComment={handleSaveComment}
                                onCancelEditComment={() => setEditingComment(null)}
                            />
                        ))}

                        {/* Load More */}
                        {hasMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                    sx={{
                                        color: '#14B8A6',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        '&:hover': { backgroundColor: '#F0FDFA' }
                                    }}
                                >
                                    טען עוד...
                                </Button>
                            </Box>
                        )}

                        {/* Total count */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 3,
                            pt: 2,
                            borderTop: '1px solid #E2E8F0'
                        }}>
                            <Typography sx={{ color: '#64748B', fontSize: '14px' }}>
                                סה״כ סרטונים:
                            </Typography>
                            <Typography sx={{ color: '#1E293B', fontSize: '14px', fontWeight: 600 }}>
                                {videos.length}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>

            {/* Upload Dialog */}
            <UploadDialog
                open={uploadDialogOpen}
                onClose={handleUploadDialogClose}
                initialDate={uploadPrefilledDate}
                traineeId={userId}
                onUploadComplete={handleUploadComplete}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                PaperProps={{ sx: { borderRadius: '12px', direction: 'rtl' } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '16px', color: '#1E293B' }}>
                    {deleteConfirm?.type === 'single' ? 'מחיקת סרטון' : 'מחיקת כל הסרטונים מהתאריך'}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#64748B', fontSize: '14px' }}>
                        {deleteConfirm?.type === 'single'
                            ? 'האם אתה בטוח שברצונך למחוק סרטון זה?'
                            : `האם אתה בטוח שברצונך למחוק ${deleteConfirm?.count} סרטונים מתאריך ${formatDate(deleteConfirm?.date)}?`
                        }
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button onClick={() => setDeleteConfirm(null)} sx={{ color: '#64748B', textTransform: 'none' }}>
                        ביטול
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        sx={{
                            backgroundColor: '#EF4444',
                            textTransform: 'none',
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#DC2626', boxShadow: 'none' }
                        }}
                    >
                        מחיקה
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProgressVideosPage;
